import { Color3 } from '@babylonjs/core/Maths/math.color';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import type { GrabberHub } from '../input/Grabbers';
import { cloneModelTemplate, ModelLibrary } from '../room/ModelLibrary';
import { applyTintedMaterial } from '../room/ModelMaterials';
import type { PossessionManager } from './PossessionManager';

/**
 * The off hand becomes a trash can for as long as the dominant hand holds a node
 * (plan/ui-improvements.md, item 3 — the owner: "I'd rather have the left hand turn into a
 * trash can to act as a delete button"). This replaces the off-hand tray's own `edit text` /
 * `duplicate` / `delete` row entirely (`AuthoringController`/`PaletteUI`): `duplicate` has no
 * replacement, `edit text` is reached through speech-to-text instead, and `delete` becomes this
 * — position the held node at the bin and let go.
 *
 * **Purely a rendered affordance.** It shows *where* the bin is, at the off hand's own live
 * position, but the decision of whether a release actually deletes anything belongs entirely to
 * `PossessionManager.release` (`isOverTrash`), which reads the identical off-hand grabber
 * position this class positions its own mesh at — nothing here is ever read back as a source of
 * truth for that decision. Same discipline `ToolPropRenderer` already follows for the drawn pen:
 * the renderer never feeds back into the logic it visualises.
 *
 * Shown only while `possession.heldNodeId !== null` — there is nothing to throw away otherwise,
 * and a bin hanging in the off hand at all other times would just be clutter. Not gated on the
 * off-hand grip: unlike the tray, which needed a grip to summon it, a bin the owner has to keep
 * squeezing for is not one you can casually drop something into — see
 * `AuthoringController`'s own `openSuppressed` wiring, which stops the tray from opening at all
 * while a node is held, precisely so the off hand has nothing else competing for it here.
 *
 * Renders the authored `trashcan.glb` (`preloadModels`) once it resolves — the same
 * template-or-primitive shape `ToolPropRenderer` already uses for the marker and eraser, applied
 * to a mesh that is only ever translated, never aimed or rotated, so there is no working-point
 * offset to declare the way a held prop needs one: the model's own origin sits at the base of
 * the can with the opening upward, and placing that origin at the off hand's own `position` is
 * already correct.
 */
// spec: SPEC-079
export class TrashCanRenderer {
  private mesh: Mesh | null = null;

  /** Whether the bin is currently shown, tracked separately from `mesh?.isEnabled()` so
   *  `onSetControllerModelVisible` only fires on an actual transition — see `tick`'s own use of
   *  it. Not derived from the mesh's enabled state because the mesh may not exist yet the first
   *  time this would need to be `false` (before `ensureMesh` has ever run). */
  private shown = false;

  /**
   * Forwarded to `XrManager.setOffControllerModelVisible` by the caller (main.ts) — this class
   * does not depend on XrManager or WebXR at all, the same arm's-length shape
   * `ToolPropRenderer.onSetControllerModelVisible` already uses for the dominant hand. `true`
   * means "show the off hand's controller model again"; `false` means "hide it, this bin is
   * replacing it" — reported from a headset twice before this existed: "the controller still
   * shows up inside it. It's just a floating overlay," and again after the model landed,
   * "the left controller is still visible so it didn't properly replace it."
   */
  onSetControllerModelVisible: ((visible: boolean) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly possession: PossessionManager,
    // Defaulted rather than always constructed by every caller, the same shape
    // ToolPropRenderer's own constructor uses for the identical reason: a test can supply one it
    // has already populated via ModelLibrary.loadFromBytes, while the one real caller
    // (AuthoringController.ts) takes the default and calls preloadModels() on it itself, below.
    private readonly models: ModelLibrary = new ModelLibrary(scene),
  ) {}

  /**
   * Preloads the authored trashcan.glb ahead of the first time a node is grabbed — the same
   * "before first use, not on demand" rule `ToolPropRenderer.preloadModels` follows, for the
   * identical reason: `ensureMesh` builds synchronously, on the first tick the bin is shown, so
   * whatever has not resolved by then falls back to the procedural cylinder for the rest of the
   * session. Call once at boot, before the first tick — main.ts does, alongside
   * `authoring.toolProp.preloadModels()`.
   *
   * `{ normalize: false, preserveMaterials: true }` for the same reasons every other prop here
   * uses them: the model is authored at its true, real-world size with its origin at the base of
   * the can (opening upward) — not the node catalogue's uniform TARGET_SIZE or a bounding-box
   * centre — and it carries two material slots (`Mat.Prop.Body`, `Mat.Prop.Tint`) that
   * `Mesh.MergeMeshes` would otherwise collapse into one at load time.
   */
  async preloadModels(): Promise<void> {
    const loaded = await this.models.loadFromPath(TRASHCAN_MODEL_KEY, TRASHCAN_MODEL_PATH, {
      normalize: false,
      preserveMaterials: true,
    });

    console.info(
      loaded
        ? 'Trash can preload: trashcan.glb resolved — the bin renders the authored model.'
        : 'Trash can preload: trashcan.glb did not resolve — the bin renders its procedural fallback.',
    );
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /** Whether the bin is currently shown — exposed for tests, which cannot see a rendered pixel
   *  (CLAUDE.md, "When nothing in the project can see a rendered pixel"). */
  get isShown(): boolean {
    return this.mesh?.isEnabled() ?? false;
  }

  private tick(): void {
    const off = this.grabbers.byRole('off');
    const held = this.possession.heldNodeId !== null;

    if (!off || !held) {
      this.mesh?.setEnabled(false);
      this.setShown(false);
      return;
    }

    const mesh = this.ensureMesh();
    mesh.setEnabled(true);
    mesh.position.copyFrom(off.position);
    this.setShown(true);
  }

  /**
   * Applies `onSetControllerModelVisible` only on a genuine shown/hidden transition, not every
   * tick — the same "only act on a change" discipline `XrManager.syncTravelSuppression` and
   * `ToolPropRenderer.syncControllerVisibility` both already follow, here for the simpler reason
   * that this class has only one hand and one shown/not-shown state to track, not a per-hand map.
   */
  private setShown(shown: boolean): void {
    if (shown === this.shown) {
      return;
    }

    this.shown = shown;
    this.onSetControllerModelVisible?.(!shown);
  }

  private ensureMesh(): Mesh {
    if (this.mesh) {
      return this.mesh;
    }

    const template = this.models.templateFor(TRASHCAN_MODEL_KEY);

    if (template) {
      this.mesh = this.buildModelMesh(template);
      return this.mesh;
    }

    const mesh = CreateCylinder(
      'trash-can',
      {
        diameterTop: BIN_DIAMETER_TOP,
        diameterBottom: BIN_DIAMETER_BOTTOM,
        height: BIN_HEIGHT,
        tessellation: 16,
      },
      this.scene,
    );

    mesh.isPickable = false;

    const material = new StandardMaterial('trash-can-material', this.scene);
    const tint = Color3.FromHexString(BIN_COLOR);
    material.diffuseColor = tint;
    material.emissiveColor = tint.scale(0.3);
    material.specularColor = Color3.Black();
    mesh.material = material;

    this.mesh = mesh;
    return mesh;
  }

  /**
   * The authored trashcan.glb, cloned and tinted to `BIN_COLOR` on its own `Mat.Prop.Tint`
   * accent slot — the identical clone/tint/fallback shape `ToolPropRenderer.buildTemplatedProp`
   * uses for the marker and eraser, applied here to a single caller rather than pulled out into
   * a shared helper: this class has exactly one model-backed prop, so there is only one call
   * site for the pattern to serve. `Mat.Prop.Body` (the authored can colour) is left alone,
   * cloned by `applyTintedMaterial` like every other non-tint slot.
   *
   * **Origin: base of the can, opening upward** (the asset author's own handoff) — positioning
   * the clone at the off hand's own `position`, unchanged, already puts the base there with the
   * opening extending away from the hand; there is no offset to apply the way a held prop's tip
   * needs one, because nothing about this mesh is ever aimed or rotated, only translated.
   */
  private buildModelMesh(template: Mesh): Mesh {
    const mesh = cloneModelTemplate(template, 'trash-can-model');
    mesh.isPickable = false;

    const fallbackMaterial = new StandardMaterial('trash-can-model-fallback-material', this.scene);
    const tint = Color3.FromHexString(BIN_COLOR);
    fallbackMaterial.diffuseColor = tint;
    fallbackMaterial.emissiveColor = tint.scale(0.3);
    fallbackMaterial.specularColor = Color3.Black();

    const tintMaterial = applyTintedMaterial(
      this.scene,
      mesh,
      PROP_TINT_MATERIAL_NAME,
      'trash-can-model',
      tint,
      fallbackMaterial,
      () => warnMissingTintSlot(),
    );

    if (tintMaterial) {
      // The tint slot resolved, so fallbackMaterial was never assigned anywhere — dispose it now
      // rather than leave it unreferenced in the scene for the rest of the session (the same
      // "dispose the loser" discipline ToolPropRenderer.buildTemplatedProp already follows).
      fallbackMaterial.dispose();
    }

    return mesh;
  }

  /**
   * Drops whatever is shown, and releases the off-hand controller model if this class is the
   * one currently hiding it — a teardown mid-hold (session end, scene rebuild) must not leave
   * the hand permanently invisible, the exact bug the "restore path" half of this feature exists
   * to avoid.
   */
  dispose(): void {
    // Read off the mesh's own live material, not a locally-held reference — for a model-backed
    // bin whose tint slot resolved, the mesh carries a per-instance MultiMaterial rather than a
    // single StandardMaterial, and disposing it as one requires the three-argument form (CLAUDE.md,
    // "That same cascade does not free materials" — the identical shape
    // ToolPropRenderer.disposeMesh already uses for the marker's own MultiMaterial).
    const meshMaterial = this.mesh?.material;

    if (meshMaterial instanceof MultiMaterial) {
      meshMaterial.dispose(false, false, true);
    } else {
      meshMaterial?.dispose();
    }

    this.mesh?.dispose();
    this.mesh = null;
    this.setShown(false);
  }
}

/** A hand-sized bin — wide enough to read as "drop it here," small enough not to swallow the
 *  view of whatever is being carried toward it. */
const BIN_DIAMETER_TOP = 0.14;
const BIN_DIAMETER_BOTTOM = 0.1;
const BIN_HEIGHT = 0.12;

/**
 * The colour `delete` used in the tray it replaces, so the association carries over. Exported
 * since PR #153's carousel (`ReadoutOption.danger`, `input/ModeDefinitions.ts`) already reuses it
 * for Link mode's "remove" style position, and `LinkPreviewRenderer`'s own muzzle preview
 * (`LinkPreviewRenderer.ts`) reuses it again for the identical reason — one colour means "this
 * deletes something with no undo" everywhere in this product, not a per-feature red picked afresh
 * each time (CLAUDE.md's own instruction, given directly for this task: "reuse it again rather
 * than inventing a third red").
 */
export const BIN_COLOR = '#FF5D8F';

/** The authored trash can model — opaque, `doubleSided: false`, Draco compressed, no textures
 *  or rig, same as every other prop under `public/props/` (the asset author's own handoff).
 *  Origin at the base of the can; opens upward. */
const TRASHCAN_MODEL_KEY = 'trashcan';
const TRASHCAN_MODEL_PATH = '/props/trashcan.glb';

/** The exact name of the trash can's per-instance accent slot — the same convention every other
 *  prop uses (`ToolPropRenderer`'s own `PROP_TINT_MATERIAL_NAME`, not imported from there:
 *  duplicated as a literal rather than shared across two files for one three-word string, the
 *  same call every other small per-file constant in this project already makes). Matched by
 *  name, not primitive index, by `applyTintedMaterial` itself. */
const PROP_TINT_MATERIAL_NAME = 'Mat.Prop.Tint';

/** Whether the missing-`Mat.Prop.Tint`-slot warning has already been logged this session — this
 *  class only ever loads one model-backed prop under one key, so a single boolean is enough;
 *  `ToolPropRenderer`'s equivalent needs a `Set` because it dedupes across several keys. */
let warnedMissingTintSlot = false;

/** Logs once that the trash can model has no `Mat.Prop.Tint` sub-material — an older or
 *  hand-edited export. Never thrown: the bin must render regardless, just tinted as a whole
 *  mesh instead of on its own accent slot (`applyTintedMaterial`'s own documented fallback). */
function warnMissingTintSlot(): void {
  if (warnedMissingTintSlot) {
    return;
  }

  warnedMissingTintSlot = true;
  console.warn(
    `Prop model "${TRASHCAN_MODEL_KEY}.glb" has no "${PROP_TINT_MATERIAL_NAME}" material slot — ` +
      'it falls back to a single whole-mesh tint instead of a per-slot accent.',
  );
}
